import { useEffect, useCallback } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useAppState } from "@/modules/app/useAppContext";
import { usePlaygroundDispatch, usePlaygroundState } from "../usePlaygroundContext";
import { setUserView, updateUserView } from "../playgroundSlice";
import { PlaygroundUserViewEntities, PlaygroundContext } from "../types";

// Custom event name for session creation
export const PLAYGROUND_SESSION_CREATED_EVENT = "playground:session-created";

// Custom event detail type
export interface PlaygroundSessionCreatedDetail {
  sessionId: string;
}

// Map route patterns to entity types
// NOTE: More specific patterns MUST come BEFORE more general ones
// e.g., /query/snowflake-jobs/:workflowId BEFORE /query/:queryId
const routeContextConfig: Record<string, PlaygroundUserViewEntities> = {
  // Snowflake Workloads - must be before /query/:queryId to match first
  "/query/snowflake-jobs": PlaygroundUserViewEntities.SNOWFLAKE_JOBS_LIST,
  "/query/snowflake-jobs/:workflowId": PlaygroundUserViewEntities.SNOWFLAKE_JOB,
  "/query/snowflake-jobs/:workflowId/:runId": PlaygroundUserViewEntities.SNOWFLAKE_JOB,
  "/query/snowflake-jobs/:workflowId/compare": PlaygroundUserViewEntities.SNOWFLAKE_JOB,
  // Code page tabs - must be before /query/:queryId to match first
  "/query/all": PlaygroundUserViewEntities.SNOWFLAKE_QUERIES_LIST,
  "/query/ai-services": PlaygroundUserViewEntities.AI_SERVICES,
  "/query/ai-services/:serviceType": PlaygroundUserViewEntities.AI_SERVICES,
  "/query/dbt-jobs": PlaygroundUserViewEntities.DBT_JOB,
  "/query/dbt-jobs/:jobId": PlaygroundUserViewEntities.DBT_JOB,
  "/query/tableau-jobs": PlaygroundUserViewEntities.TABLEAU_JOB,
  "/query/tableau-jobs/:jobId": PlaygroundUserViewEntities.TABLEAU_JOB,
  "/query/stored-procedures": PlaygroundUserViewEntities.STORED_PROCEDURE,
  "/query/stored-procedures/:hash": PlaygroundUserViewEntities.STORED_PROCEDURE,
  "/query/streamlit": PlaygroundUserViewEntities.STREAMLIT,
  "/query/streamlit/:hash": PlaygroundUserViewEntities.STREAMLIT,
  "/query/notebooks": PlaygroundUserViewEntities.NOTEBOOK,
  "/query/notebooks/:hash": PlaygroundUserViewEntities.NOTEBOOK,
  "/query/groups/:queryHash": PlaygroundUserViewEntities.QUERY_GROUP,
  // Generic query - matches /query/:queryId after workload routes
  "/query/:queryId": PlaygroundUserViewEntities.SNOWFLAKE_QUERY,
  "/datasets": PlaygroundUserViewEntities.SNOWFLAKE_DATASET,
  "/datasets/:tableId": PlaygroundUserViewEntities.SNOWFLAKE_DATASET,
  "/tables": PlaygroundUserViewEntities.SNOWFLAKE_TABLE,
  "/warehouse": PlaygroundUserViewEntities.SNOWFLAKE_WAREHOUSE,
  "/warehouse/:instance_id/:warehouse_rk": PlaygroundUserViewEntities.SNOWFLAKE_WAREHOUSE,
  "/users": PlaygroundUserViewEntities.SNOWFLAKE_USERS,
  "/roles": PlaygroundUserViewEntities.SNOWFLAKE_ROLES,
  "/summary": PlaygroundUserViewEntities.SUMMARY,
  "/team-spends": PlaygroundUserViewEntities.TEAM_SPENDS,
  "/accounts-cost": PlaygroundUserViewEntities.ACCOUNTS_COST,
  "/discover": PlaygroundUserViewEntities.DISCOVER,
  "/dbt_models/:dbtModelId": PlaygroundUserViewEntities.DBT_MODEL,
  "/experimentation/:experimentId": PlaygroundUserViewEntities.EXPERIMENT,
  "/incidents/:incidentId": PlaygroundUserViewEntities.INCIDENT,
  "/contracts/:contractId": PlaygroundUserViewEntities.CONTRACT,
  "/compute-pools/:poolName": PlaygroundUserViewEntities.COMPUTE_POOL,
  "/spaces": PlaygroundUserViewEntities.SPACE,
  "/spaces/:id": PlaygroundUserViewEntities.SPACE,
  "/checks/:alertId": PlaygroundUserViewEntities.ALERT,
  "/governance/dbtconfig/:id": PlaygroundUserViewEntities.DBT_CONFIG,
  "/collaboration/query-result/:id": PlaygroundUserViewEntities.QUERY_RESULT,
  "/collaboration/dbt-docs/:shareId": PlaygroundUserViewEntities.DBT_DOCS,
  "/collaboration/export-lineage/:itemId": PlaygroundUserViewEntities.EXPORT_LINEAGE,
  "/settings/datastores/:datastoreId": PlaygroundUserViewEntities.DATASTORE,
  "/datamates": PlaygroundUserViewEntities.TEAMMATE,
  "/datamates/memories/:id": PlaygroundUserViewEntities.MCP_MEMORY,
  "/datamate/:teammateId": PlaygroundUserViewEntities.TEAMMATE,
  "/teammate/:teammateId": PlaygroundUserViewEntities.TEAMMATE,
  "/usergroups/:id": PlaygroundUserViewEntities.USER_GROUP,
  "/bi-reports/:id": PlaygroundUserViewEntities.BI_REPORT,
  "/bi-reports/users/:id": PlaygroundUserViewEntities.BI_USER_REPORT,
  "/knowledge-hub": PlaygroundUserViewEntities.KNOWLEDGE_SOURCE,
  "/knowledge-hub/:id": PlaygroundUserViewEntities.KNOWLEDGE_SOURCE,
  // Databricks routes (merged features, root-level paths)
  "/sql-warehouses": PlaygroundUserViewEntities.DATABRICKS_WAREHOUSE,
  "/sql-warehouses/:warehouseId": PlaygroundUserViewEntities.DATABRICKS_WAREHOUSE,
  "/clusters": PlaygroundUserViewEntities.DATABRICKS_CLUSTER,
  "/clusters/:clusterId": PlaygroundUserViewEntities.DATABRICKS_CLUSTER,
  "/cost-breakdown": PlaygroundUserViewEntities.DATABRICKS_SKU_COST,
  "/queries": PlaygroundUserViewEntities.DATABRICKS_QUERY,
  "/queries/:queryId": PlaygroundUserViewEntities.DATABRICKS_QUERY,
  // Databricks jobs - more specific patterns listed first
  "/jobs/:jobId/runs/:runId/tasks/:taskId/spark-job/:sparkJobId":
    PlaygroundUserViewEntities.DATABRICKS_JOB,
  "/jobs/:jobId/runs/:runId/tasks/:taskId": PlaygroundUserViewEntities.DATABRICKS_JOB,
  "/jobs/:jobId/runs/:runId": PlaygroundUserViewEntities.DATABRICKS_JOB,
  "/jobs/:jobId/tasks/:taskId": PlaygroundUserViewEntities.DATABRICKS_JOB,
  "/jobs/:jobId": PlaygroundUserViewEntities.DATABRICKS_JOB,
  "/jobs": PlaygroundUserViewEntities.DATABRICKS_JOB,
  // Databricks AI/ML - more specific patterns listed first
  "/ai-ml/model-serving/:endpointId": PlaygroundUserViewEntities.DATABRICKS_MODEL_SERVING,
  "/ai-ml/vector-search/:endpointId": PlaygroundUserViewEntities.DATABRICKS_VECTOR_SEARCH,
  "/ai-ml": PlaygroundUserViewEntities.DATABRICKS_AIML,
};

// Convert route pattern to regex for matching
const patternToRegex = (pattern: string): RegExp => {
  const regexPattern = pattern.replace(/:[^/]+/g, "[^/]+");
  return new RegExp(`^${regexPattern}(?:/.*)?$`);
};

// Count path segments in a pattern (more segments = more specific)
const getPatternSpecificity = (pattern: string): number => {
  return pattern.split("/").filter(Boolean).length;
};

const findMatchingEntityType = (pathname: string): PlaygroundUserViewEntities | null => {
  let bestMatch: { entityType: PlaygroundUserViewEntities; specificity: number } | null = null;

  for (const [pattern, entityType] of Object.entries(routeContextConfig)) {
    if (patternToRegex(pattern).test(pathname)) {
      const specificity = getPatternSpecificity(pattern);
      // Keep the most specific match (most path segments)
      if (!bestMatch || specificity > bestMatch.specificity) {
        bestMatch = { entityType, specificity };
      }
    }
  }

  return bestMatch?.entityType ?? null;
};

/**
 * Captures the current route context to be used in playground session
 * @param pathname - Current route pathname from useLocation()
 * @param params - Route params from useParams() (called from within route context)
 * @param isDatabricksTenant - Whether the current tenant is a Databricks tenant (for routes
 *   whose rendered component differs by flag, e.g., /users → DatabricksUsers vs Governance)
 * @returns user_view object to be stored in session context
 */
// Pages use `start_date` / `end_date` URL search params (per `useDateFromSearchParams`)
// to scope time-based queries. Mirror that into user_view so the Studio agent
// answers for the same window the user sees, not an invented default.
// When the URL has no dates, fall back to the same 28-day default that
// `useDateFromSearchParams` uses — otherwise the agent has no idea what
// window the user is looking at until they touch the date picker.
const extractDateRangeFromSearch = (
  search: URLSearchParams
): Pick<PlaygroundContext["user_view"], "start_date" | "end_date"> => {
  const startDate = search.get("start_date") ?? dayjs().subtract(28, "days").toDate().toISOString();
  const endDate = search.get("end_date") ?? dayjs().toDate().toISOString();
  return { start_date: startDate, end_date: endDate };
};

const captureRouteContext = (
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
  search: URLSearchParams,
  isDatabricksTenant: boolean
): PlaygroundContext["user_view"] => {
  let entityType = findMatchingEntityType(pathname);

  // /users and /summary render different pages for Databricks vs Snowflake tenants;
  // the pathname-only config can't distinguish, so override here.
  if (isDatabricksTenant && pathname === "/users") {
    entityType = PlaygroundUserViewEntities.DATABRICKS_USER;
  }
  if (isDatabricksTenant && pathname === "/summary") {
    entityType = PlaygroundUserViewEntities.DATABRICKS_SUMMARY;
  }
  if (isDatabricksTenant && pathname === "/discover") {
    entityType = PlaygroundUserViewEntities.DATABRICKS_DISCOVER;
  }

  if (entityType) {
    return {
      entity_type: entityType,
      path: pathname,
      ...params,
      ...extractDateRangeFromSearch(search),
    };
  }

  return {
    entity_type: PlaygroundUserViewEntities.NONE,
  };
};

/**
 * Hook to capture route context when a new playground session is created
 * Must be called from within a route context (components inside react-router)
 * Listens for custom session creation events and updates user view
 */
export const usePlaygroundRoute = () => {
  const playgroundDispatch = usePlaygroundDispatch();
  const { currentSessionId } = usePlaygroundState();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { isDatabricksTenant = false } = useAppState();

  const handleSessionCreated = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<PlaygroundSessionCreatedDetail>;
      const { sessionId } = customEvent.detail;

      console.log("usePlaygroundRoute: Received session created event", sessionId);

      // Capture route context
      const routeContext = captureRouteContext(
        location.pathname,
        params,
        searchParams,
        isDatabricksTenant
      );

      playgroundDispatch(
        setUserView({
          sessionId,
          userView: routeContext,
        })
      );
    },
    [location.pathname, params, searchParams, isDatabricksTenant, playgroundDispatch]
  );

  // Listen for session creation events and update user view
  useEffect(() => {
    window.addEventListener(PLAYGROUND_SESSION_CREATED_EVENT, handleSessionCreated);

    return () => {
      window.removeEventListener(PLAYGROUND_SESSION_CREATED_EVENT, handleSessionCreated);
    };
  }, [handleSessionCreated]);

  // Keep the active session's date range in sync when the user changes the
  // page's date filter mid-session. `setUserView` is a once-per-session guard,
  // so use `updateUserView` for partial merges.
  useEffect(() => {
    if (!currentSessionId) return;
    const dateRange = extractDateRangeFromSearch(searchParams);
    playgroundDispatch(
      updateUserView({
        sessionId: currentSessionId,
        userView: dateRange,
      })
    );
  }, [currentSessionId, searchParams, playgroundDispatch]);
};
